import { Box, ResponsiveContext, Text } from 'grommet';
import React from 'react';

import { isNewDisplayWeek } from '../../utils/postTournamentWindow';

import {
  PgaTournament,
  PgaTournamentRoundStatusEnum,
  PgaTournamentTournamentStatusEnum,
} from '@drewkimberly/pga-pool-api';

export interface TournamentHeaderProps {
  tournament: PgaTournament;
  round?: number;
  scoresAreOfficial?: boolean;
}

export function TournamentHeader({ tournament, round, scoresAreOfficial }: TournamentHeaderProps) {
  const size = React.useContext(ResponsiveContext);
  const isDesktop = size !== 'small';
  const circleSize = isDesktop ? '116px' : '90px';
  const imgSize = isDesktop ? '110px' : '78px';
  const status = resolveStatus(tournament, round, scoresAreOfficial);

  const locationParts: string[] = [];
  if (tournament.course_name) locationParts.push(tournament.course_name);
  if (tournament.city && tournament.state) {
    locationParts.push(`${tournament.city}, ${tournament.state}`);
  }

  return (
    <Box gap="small" pad={{ vertical: 'small' }}>
      <Box direction="row" gap={isDesktop ? 'medium' : 'small'}>
        {tournament.logo_url && (
          <Box
            flex={false}
            width={circleSize}
            height={circleSize}
            round="full"
            overflow="hidden"
            border={{ size: 'xsmall', color: 'light-4' }}
            background="white"
            align="center"
            justify="center"
            style={{ flexShrink: 0, alignSelf: 'center' }}
          >
            <img
              src={tournament.logo_url}
              alt=""
              style={{
                width: imgSize,
                height: imgSize,
                objectFit: 'contain',
              }}
            />
          </Box>
        )}

        <Box flex gap="xsmall" justify="center">
          <Text
            size={isDesktop ? 'xlarge' : 'large'}
            weight="bold"
            style={{ fontFamily: 'var(--font-display)', lineHeight: 1.2 }}
          >
            {tournament.name}
          </Text>

          {/* Location line */}
          <Text size="small" color="text-weak">
            {locationParts.join(' \u00B7 ')}
          </Text>

          {/* Date */}
          <Text size="small" style={{ fontStyle: 'italic' }} color="text-weak">
            {tournament.date.display}
          </Text>

          {/* Status badge */}
          <Box direction="row">
            <InlineStatusBadge status={status} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

interface ResolvedStatus {
  label: string;
  variant: 'live' | 'official' | 'thisweek' | 'upcoming' | 'pending';
}

function resolveRoundSuffix(roundStatus: PgaTournament['round_status']): string {
  switch (roundStatus) {
    case PgaTournamentRoundStatusEnum.InProgress:
      return 'LIVE';
    case PgaTournamentRoundStatusEnum.Suspended:
      return 'SUSPENDED';
    case PgaTournamentRoundStatusEnum.Complete:
    case PgaTournamentRoundStatusEnum.Official:
      return 'COMPLETE';
    case PgaTournamentRoundStatusEnum.Upcoming:
    case PgaTournamentRoundStatusEnum.GroupingsOfficial:
    default:
      return 'STARTING SOON';
  }
}

function resolveStatus(
  tournament: PgaTournament,
  round?: number,
  scoresAreOfficial?: boolean
): ResolvedStatus {
  const { tournament_status, round_status, current_round } = tournament;
  const displayRound = round ?? current_round;

  if (tournament_status === PgaTournamentTournamentStatusEnum.InProgress) {
    const roundLabel = displayRound ? `ROUND ${displayRound}` : '';
    const suffix = resolveRoundSuffix(round_status);

    return {
      label: [roundLabel, suffix].filter(Boolean).join(' \u00B7 '),
      variant: 'live',
    };
  }

  if (tournament_status === PgaTournamentTournamentStatusEnum.Completed) {
    if (scoresAreOfficial === false) {
      return { label: 'Scores Pending', variant: 'pending' };
    }
    return { label: 'OFFICIAL', variant: 'official' };
  }

  // NOT_STARTED: "This Week" only after the post-tournament window closes (Mon 8 AM CT)
  const startDate = new Date(tournament.date.start);
  const now = new Date();
  const daysUntilStart = (startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

  if (daysUntilStart <= 7 && isNewDisplayWeek()) {
    return { label: 'THIS WEEK', variant: 'thisweek' };
  }

  return { label: 'UPCOMING', variant: 'upcoming' };
}

const BADGE_STYLES: Record<
  ResolvedStatus['variant'],
  { color: string; bg: string; textColor: string }
> = {
  live: {
    color: 'var(--color-status-live)',
    bg: 'var(--color-status-live-bg)',
    textColor: 'var(--color-status-live)',
  },
  official: {
    color: 'var(--color-status-official)',
    bg: 'var(--color-status-official-bg)',
    textColor: '#ffffff',
  },
  thisweek: {
    color: 'var(--color-status-thisweek)',
    bg: 'var(--color-status-thisweek-bg)',
    textColor: 'var(--color-status-thisweek)',
  },
  upcoming: {
    color: 'var(--color-status-upcoming)',
    bg: 'var(--color-status-upcoming-bg)',
    textColor: 'var(--color-status-upcoming)',
  },
  pending: {
    color: 'var(--color-status-pending)',
    bg: 'var(--color-status-pending-bg)',
    textColor: 'var(--color-status-pending)',
  },
};

interface InlineStatusBadgeProps {
  status: ResolvedStatus;
}

function InlineStatusBadge({ status }: InlineStatusBadgeProps) {
  const styles = BADGE_STYLES[status.variant];

  return (
    <Box
      direction="row"
      align="center"
      gap="xxsmall"
      pad={{ horizontal: 'xsmall', vertical: 'xxsmall' }}
      round="xsmall"
      flex={false}
      style={{
        backgroundColor: styles.bg,
        borderColor: styles.color,
        borderWidth: '1px',
        borderStyle: 'solid',
      }}
    >
      <Text
        size="xsmall"
        weight="bold"
        style={{ color: styles.textColor, letterSpacing: '0.04em', fontSize: '0.75rem' }}
      >
        {status.label}
      </Text>
    </Box>
  );
}
